import React from "react";
import { Typography } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { SbTag } from "uiCore";
import { formatTimestamp, toTimestampMs } from "Utils/timeFormat";
import { ProjectNameCellStyled } from "./orgDesign.styles";
import { BASELINE_TAG, ENTRY_OPTION, getFilters } from "./orgDesign.constants";

const { Text } = Typography;

const entryOptionForSourceType = (sourceType) =>
  sourceType === "PAIC" ? ENTRY_OPTION.PAIC_RECOMMENDED : ENTRY_OPTION.EXISTING_ORG;

// Fields match GET /versions verbatim (Org Redesign API doc §8) — a
// lightweight, org-wide list of PUBLISHED versions only. Built from
// `versions` (not a module-level constant) so filters reflect whatever
// values actually appear in the current list. Shared by the Org Design
// landing's "Previous Iterations" and Talent Mobility's PAIC Org Redesign.
//
// Column order follows BUG #1800, with the single "Version" column BUG #1940
// then split in two: Project Name, Org Version, PAIC Version, Source,
// Status, Created By, Updated, Published. sb_org_redesign_code has no
// dedicated column of its own here (that order leaves no room for one) — it
// renders as a muted second line under the project name instead, so it's
// still visible without disturbing the requested layout.
const getCreatedByName = (record) =>
  [record.triggered_by_user_first_name, record.triggered_by_user_last_name]
    .filter(Boolean)
    .join(" ");

const formatCreatedBy = (record) => getCreatedByName(record) || <Text type="secondary">—</Text>;

const formatVersionLabel = (v) => `Version ${v}`;

// org_redesign_name is the user-typed name from Save Version (either source
// type); project_name is PAIC-only (the PAIC group's name) and always null
// for CURRENT ORG — so a named version shows its own name, and an unnamed
// PAIC one still falls back to its group's.
export const displayProjectName = (record) => record.org_redesign_name || record.project_name;

export const formatVersionRowLabel = (record) =>
  `${displayProjectName(record) ? `${displayProjectName(record)} — ` : ""}${formatVersionLabel(record.version)}`;

const renderVersion = (v) => (v != null ? <Text strong>{formatVersionLabel(v)}</Text> : <Text type="secondary">—</Text>);

// `splitVersion: false` keeps the pre-#1940 single "Version" column (the Org
// Design's own iteration number) for screens that don't need the PAIC run.
export const buildVersionColumns = (versions, { splitVersion = true } = {}) => [
  {
    title: "Project Name",
    dataIndex: "project_name",
    filters: getFilters(versions, "project_name", displayProjectName),
    onFilter: (value, record) => displayProjectName(record) === value,
    render: (_, record) => (
      <ProjectNameCellStyled>
        <span>{displayProjectName(record) || <Text type="secondary">—</Text>}</span>
        {record.sb_org_redesign_code && <span className="code-text">{record.sb_org_redesign_code}</span>}
      </ProjectNameCellStyled>
    ),
  },
  // `version` is the Org Design's own iteration number — 1 for a PAIC-
  // sourced row today, but can climb past 1 for a from-scratch (CURRENT ORG)
  // design that's been saved over multiple times (BUG #1940).
  {
    title: splitVersion ? "Org Version" : "Version",
    dataIndex: "version",
    filterLabel: formatVersionLabel,
    render: renderVersion,
  },
  // `selection_version` is which PAIC agent-selection run this design was
  // generated from — only PAIC-sourced rows carry one; a from-scratch
  // (CURRENT ORG) row never had a PAIC selection to begin with, so this is
  // always blank for it (BUG #1940).
  ...(splitVersion
    ? [
        {
          title: "PAIC Version",
          dataIndex: "selection_version",
          filterLabel: formatVersionLabel,
          render: renderVersion,
        },
      ]
    : []),
  {
    title: "Source",
    dataIndex: "source_type",
    // Auto filter, labelled like the tag rather than the raw source_type.
    filterLabel: (v) => BASELINE_TAG[entryOptionForSourceType(v)],
    render: (v) => {
      const entryOption = entryOptionForSourceType(v);
      return <SbTag color={entryOption === ENTRY_OPTION.PAIC_RECOMMENDED ? "processing" : "default"}>{BASELINE_TAG[entryOption]}</SbTag>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    filters: getFilters(versions, "status"),
    onFilter: (value, record) => record.status === value,
    render: (v) => <SbTag color="success">{v}</SbTag>,
  },
  {
    title: "Created By",
    key: "created_by",
    filterValue: getCreatedByName,
    render: (_, record) => formatCreatedBy(record),
  },
  {
    title: "Updated",
    dataIndex: "last_updated_time_stamp",
    sorter: (a, b) => toTimestampMs(a.last_updated_time_stamp) - toTimestampMs(b.last_updated_time_stamp),
    // Most recently updated first on load; the header still toggles it.
    defaultSortOrder: "descend",
    render: (v) => formatTimestamp(v) || <Text type="secondary">—</Text>,
  },
  {
    title: "Published",
    dataIndex: "published_status",
    // Every row in this list is published (the cell ignores the raw value).
    filterValue: () => "Published",
    render: () => (
      <SbTag color="success" icon={<CheckCircleFilled />}>Published</SbTag>
    ),
  },
];
